import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Download, RefreshCw, AlertTriangle, MapPin, Activity, FileText, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminExecutiveReport, adminExecutiveReportCsvBlob, adminExecutiveReportPdfBlob } from "../lib/api";
import { EMERGENCY_FR } from "../i18n.jsx";
import { TimeFilterBar } from "./admin/TimeFilterBar";
import { buildTimeParams } from "../lib/timeFilter";
import { useCountryConfig } from "../lib/useCountryConfig";

// Module-level chart constants — hoisted out of the component render
// closure so we don't allocate new object references on every paint
// (Recharts diff-compares these and re-renders descendants otherwise).
const _CHART_MARGIN = { top: 4, right: 4, bottom: 4, left: 0 };
const _CHART_TICK   = { fontSize: 9, fill: "#71717a" };
const _CHART_TOOLTIP_CONTENT = { background: "#0b0f14", border: "1px solid #27272a", fontSize: 11 };
const _CHART_TOOLTIP_LABEL   = { color: "#a1a1aa" };
const _CHART_TOOLTIP_ITEM    = { color: "#10B981" };
const _CHART_TOOLTIP_FORMATTER = (v) => [v, "Alertes"];

/**
 * Executive Reports tab — aggregated alert statistics for SOS Mali admins.
 *
 * Filters: today / 24h / 7d / 30d / 90d / custom (since+until ISO).
 * Metrics: totals, by-type, internal/user-facing status mix, resolution
 * rate, avg ack & resolve time, top regions (Haversine nearest city),
 * trend chart, CSV export.
 *
 * Strict no-regression: this tab only READS data; no alert mutation,
 * no auth change. Reuses recharts + i18n already present in the admin
 * dashboard so the bundle doesn't grow.
 */

const PRESETS_LEGACY_UNUSED = [
  // Kept for grep history — the actual preset list now lives in
  // `/app/frontend/src/lib/timeFilter.js` (`TIME_PRESETS`) and is
  // rendered by the shared `<TimeFilterBar>` component.
];

const STATUS_LABELS = {
  received:        "Reçues",
  under_review:    "En examen",
  in_process:      "En traitement",
  help_on_the_way: "Secours assignés",
  resolved:        "Résolues",
  suspect:         "Suspectes",
  rejected_false:  "Rejetées (fausses)",
  closed:          "Clôturées",
};

const STATUS_COLORS = {
  received:        "#22d3ee",
  under_review:    "#f59e0b",
  in_process:      "#3b82f6",
  help_on_the_way: "#10b981",
  resolved:        "#22c55e",
  suspect:         "#fb923c",
  rejected_false:  "#ef4444",
  closed:          "#94a3b8",
};

const TYPE_COLOR = {
  Medical: "#dc2626", Fire: "#f97316", Police: "#3b82f6", FAMA: "#84cc16",
  Securite: "#06b6d4", Violence: "#a855f7", Accident: "#eab308", Flood: "#0ea5e9",
  Missing: "#f59e0b", Report: "#64748b",
};

const fmtSeconds = (s) => {
  if (s == null) return "—";
  if (s < 60) return `${Math.round(s)} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return m ? `${h} h ${m} min` : `${h} h`;
};

const pct = (r) => `${Math.round((r || 0) * 100)} %`;

export const ExecutiveReportsTab = () => {
  const cfg = useCountryConfig();
  const brand = cfg?.brand_name || "SOS Mali";
  const brandSlug = brand.toLowerCase().replace(/[^a-z0-9]+/g, "");
  const [preset, setPreset] = useState("7d");
  const [customSince, setCustomSince] = useState("");
  const [customUntil, setCustomUntil] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [error, setError] = useState(null);

  const params = useMemo(
    () => buildTimeParams(preset, customSince, customUntil),
    [preset, customSince, customUntil],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await adminExecutiveReport(params);
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail || "Chargement du rapport échoué");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { Promise.resolve().then(load); }, [load]);

  // Plain async event handler — mirrors the AdminLoginPage / HomePage
  // pattern used elsewhere in this codebase.
  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await adminExecutiveReportCsvBlob(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${brandSlug}-rapport-${preset}-${new Date().toISOString().slice(0, 16).replace(":", "")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV exporté");
    } catch (e) {
      toast.error("Export CSV échoué");
    } finally {
      setExporting(false);
    }
  };

  // PDF SITREP — government-grade French executive briefing. Same blob
  // download pattern as the CSV path so the admin bearer token never
  // ends up in the URL bar / browser history.
  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const blob = await adminExecutiveReportPdfBlob(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${brandSlug}-sitrep-${preset}-${new Date().toISOString().slice(0, 16).replace(":", "")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Rapport PDF téléchargé");
    } catch (e) {
      toast.error("Export PDF échoué");
    } finally {
      setExportingPdf(false);
    }
  };

  const totals = data?.totals;
  const rates = data?.rates;
  const respTime = data?.response_time;
  const location = data?.location;
  const trend = data?.trend || [];

  // Pre-sort the by_type / by_internal_status histograms once per data
  // refresh — the inline {Object.entries(...).filter.sort.map} chain
  // used to run on every paint, which got expensive once the data
  // started shipping 8+ categories. Memoised by the source dict ref.
  const typeEntries = useMemo(() => {
    const src = totals?.by_type;
    if (!src) return [];
    return Object.entries(src)
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([k, v]) => ({
        key: k, label: EMERGENCY_FR[k]?.label || k, value: v,
        color: TYPE_COLOR[k] || "#64748b",
      }));
  }, [totals?.by_type]);

  const statusEntries = useMemo(() => {
    const src = totals?.by_internal_status;
    if (!src) return [];
    return Object.entries(src)
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([k, v]) => ({
        key: k, label: STATUS_LABELS[k] || k, value: v,
        color: STATUS_COLORS[k] || "#94a3b8",
      }));
  }, [totals?.by_internal_status]);

  return (
    <div className="px-3 sm:px-5 py-4 pb-10 space-y-4" data-testid="admin-reports-tab">
      {/* Header + filters */}
      <div className="border border-zinc-800 bg-[#0f1318] p-3 sm:p-4 space-y-3" data-testid="reports-filter-bar">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-emerald-400" strokeWidth={2.4} />
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-300">
              Rapport exécutif · {brand}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="reports-refresh"
              className="border border-zinc-700 hover:border-emerald-500 text-zinc-300 hover:text-emerald-400 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.15em] flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting || loading || !data}
              data-testid="reports-export-csv"
              className="border border-emerald-500/60 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.15em] flex items-center gap-1 disabled:opacity-50"
            >
              {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
              CSV
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exportingPdf || loading || !data}
              data-testid="reports-export-pdf"
              title="Télécharger le Rapport PDF — Briefing Exécutif"
              className="border border-amber-500/60 bg-amber-500/15 text-amber-100 hover:bg-amber-500/25 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.15em] flex items-center gap-1 disabled:opacity-50"
            >
              {exportingPdf ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
              PDF
            </button>
          </div>
        </div>

        {/* Iter80 — shared time-filter component (also used by
            Tableau + Résumé analytique + Alertes). Adds "Dernière
            heure" and "Hier" that the legacy inline row lacked. */}
        <TimeFilterBar
          preset={preset}
          customSince={customSince}
          customUntil={customUntil}
          onChange={({ preset: p, customSince: s, customUntil: u }) => {
            setPreset(p);
            setCustomSince(s ?? "");
            setCustomUntil(u ?? "");
          }}
          testidPrefix="reports"
        />
      </div>

      {error && (
        <div className="border border-rose-500/50 bg-rose-500/10 text-rose-200 p-3 text-xs flex items-center gap-2" data-testid="reports-error">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {loading && !data && (
        <div className="py-12 text-center text-xs text-zinc-500 flex items-center justify-center gap-2" data-testid="reports-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Calcul du rapport…
        </div>
      )}

      {data && (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="reports-kpi-strip">
            <KPI label="Total" value={totals.total} testid="kpi-total" accent="#22d3ee" />
            <KPI label="Résolues" value={totals.resolved} sub={pct(rates.resolution_rate)} testid="kpi-resolved" accent="#22c55e" />
            <KPI label="Fausses" value={totals.is_false} sub={pct(rates.false_rate)} testid="kpi-false" accent="#ef4444" />
            <KPI label="Suspectes" value={totals.flagged} testid="kpi-flagged" accent="#fb923c" />
            <KPI label="Temps ack moy." value={fmtSeconds(respTime.avg_ack_seconds)} sub={`${respTime.ack_samples} échant.`} testid="kpi-ack" accent="#a855f7" />
            <KPI label="Temps résol. moy." value={fmtSeconds(respTime.avg_resolve_seconds)} sub={`${respTime.resolve_samples} échant.`} testid="kpi-resolve" accent="#84cc16" />
            <KPI label="Rejetées" value={totals.rejected_false} testid="kpi-rejected" accent="#9ca3af" />
            <KPI
              label="Sans GPS" value={location.no_geo} sub="géolocalisation absente"
              testid="kpi-no-geo" accent="#64748b"
            />
          </div>

          {/* Trend */}
          <div className="border border-zinc-800 bg-[#0f1318] p-3" data-testid="reports-trend">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-400 flex items-center gap-2">
                <Activity className="w-3.5 h-3.5 text-emerald-400" />
                Tendance · {trend.length} {data.bucket === "hour" ? "heures" : "jours"}
              </div>
              <div className="text-[10px] font-mono text-zinc-500">généré {new Date(data.generated_at).toLocaleString("fr-FR")}</div>
            </div>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={_CHART_MARGIN}>
                  <defs>
                    <linearGradient id="repGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="2 2" stroke="#1f2937" vertical={false} />
                  <XAxis dataKey="label" tick={_CHART_TICK} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tick={_CHART_TICK} axisLine={false} tickLine={false} width={24} allowDecimals={false} />
                  <Tooltip contentStyle={_CHART_TOOLTIP_CONTENT}
                           labelStyle={_CHART_TOOLTIP_LABEL}
                           itemStyle={_CHART_TOOLTIP_ITEM}
                           formatter={_CHART_TOOLTIP_FORMATTER} />
                  <Area type="monotone" dataKey="count" stroke="#10B981" strokeWidth={2} fill="url(#repGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Two-column: by type + by status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <CountBlock
              title="Par catégorie"
              testid="reports-by-type"
              entries={typeEntries}
              total={totals.total}
            />
            <CountBlock
              title="Par statut interne"
              testid="reports-by-status"
              entries={statusEntries}
              total={totals.total}
            />
          </div>

          {/* Location intelligence */}
          <div className="border border-zinc-800 bg-[#0f1318] p-3" data-testid="reports-location">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-400 flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-emerald-400" />
                Intelligence de localisation
              </div>
              <span
                className="text-[9px] font-bold tracking-[0.18em] uppercase text-amber-300 border border-amber-500/40 bg-amber-500/10 px-2 py-0.5"
                data-testid="reports-city-coming-soon"
                title={location.city_grouping_note}
              >
                Ville/commune — bientôt
              </span>
            </div>
            <p className="text-[11px] text-zinc-500 mb-2">{location.city_grouping_note}</p>
            {(location.top_regions || []).length === 0 ? (
              <div className="text-xs text-zinc-500">Aucune zone identifiable sur la période.</div>
            ) : (
              <ol className="space-y-1" data-testid="reports-top-regions">
                {location.top_regions.map((r, i) => {
                  const p = totals.total > 0 ? Math.round((r.count / totals.total) * 100) : 0;
                  return (
                    <li key={r.region} className="flex items-center gap-2 text-[11px]" data-testid={`region-${i}`}>
                      <span className="font-mono text-zinc-500 w-5">{i + 1}.</span>
                      <span className="flex-1 text-zinc-200 font-medium truncate">{r.region}</span>
                      <div className="flex-1 h-1.5 bg-zinc-800">
                        <div className="h-full bg-emerald-500" style={{ width: `${p}%` }} />
                      </div>
                      <span className="font-mono text-zinc-400 w-24 text-right">
                        <span className="text-white font-bold">{r.count}</span><span className="text-zinc-600"> · {p}%</span>
                      </span>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          {data.scan_truncated && (
            <div className="text-[11px] text-amber-300 border border-amber-500/40 bg-amber-500/10 px-3 py-2" data-testid="reports-scan-truncated">
              ⚠ Échantillon limité à {data.scanned.toLocaleString()} alertes. Affinez la période pour des statistiques de localisation exhaustives.
            </div>
          )}
        </>
      )}
    </div>
  );
};

const KPI = ({ label, value, sub, testid, accent }) => (
  <div className="border border-zinc-800 bg-[#0f1318] px-3 py-2.5" data-testid={testid}>
    <div className="text-[9px] font-bold uppercase tracking-[0.25em] text-zinc-500">{label}</div>
    <div className="text-2xl font-black tracking-tight text-white tabular-nums mt-0.5" style={{ color: accent }}>{value}</div>
    {sub && <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{sub}</div>}
  </div>
);

const CountBlock = ({ title, entries, total, testid }) => (
  <div className="border border-zinc-800 bg-[#0f1318] p-3" data-testid={testid}>
    <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-2">{title}</div>
    {entries.length === 0 ? (
      <div className="text-xs text-zinc-500">Aucune donnée.</div>
    ) : (
      <div className="space-y-1.5">
        {entries.map((e) => {
          const p = total > 0 ? Math.round((e.value / total) * 100) : 0;
          return (
            <div key={e.key} className="space-y-0.5" data-testid={`${testid}-row-${e.key}`}>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-zinc-300 truncate">
                  <span className="inline-block w-2 h-2 mr-1.5 align-middle" style={{ background: e.color }} />
                  {e.label}
                </span>
                <span className="font-mono text-zinc-400 tabular-nums">
                  <span className="text-white font-bold">{e.value}</span> · {p}%
                </span>
              </div>
              <div className="h-1 bg-zinc-800">
                <div className="h-full" style={{ width: `${p}%`, background: e.color }} />
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

export default ExecutiveReportsTab;
